/**
 * sessionBridge — global imperative access to openTab for non-React code.
 *
 * Non-React code (Lexical nodes, Zustand stores) can't use hooks.
 * This thin bridge is wired from SessionContext so that imperative code
 * can open tabs without reaching for the router directly.
 *
 * Race condition handling: sessionOpenTab() may be called (e.g. from router
 * beforeLoad handlers on cold page loads) before SessionProvider has mounted
 * and called setSessionOpenTab(). Calls made while _openTab is null are queued
 * and flushed in order when setSessionOpenTab is first called.
 */
import type { TabType } from './types';

let _openTab: ((id: string, type?: TabType) => void) | null = null;
let _queue: Array<{ id: string; type: TabType | undefined }> = [];

export function setSessionOpenTab(fn: (id: string, type?: TabType) => void): void {
  _openTab = fn;
}

/**
 * Returns all queued calls (made before setSessionOpenTab was wired) and clears
 * the queue. Call this once inside the session load handler to apply queued
 * deep-link navigations to the freshly loaded state before committing it.
 */
export function drainSessionBridge(): Array<{ id: string; type: TabType | undefined }> {
  const pending = _queue;
  _queue = [];
  return pending;
}

export function sessionOpenTab(id: string, type?: TabType): void {
  if (_openTab) {
    _openTab(id, type);
  } else {
    _queue.push({ id, type });
  }
}

/** Resets all module-level state. For use in tests only. */
export function resetSessionBridge(): void {
  _openTab = null;
  _queue = [];
}
